import { useEffect, useRef, useState } from "react";

/**
 * Counts up to a target number when scrolled into view.
 * Auto-detects format: keeps any prefix (<, ~) and suffix (%, +, dk, min, M, etc.)
 * Examples: "1.2M+", "%99,7", "<3 dk", "EU+US" (non-numeric pass-through)
 */
export default function CountUp({ value, duration = 1400 }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Parse: capture prefix (non-digit), main number, suffix (non-digit)
    const match = String(value).match(/^([^0-9-]*)([\d.,]+)(.*)$/);
    if (!match) {
      setDisplay(value);
      return;
    }
    const prefix = match[1] || "";
    const numStr = match[2];
    const suffix = match[3] || "";
    const decimalSep = numStr.includes(",") ? "," : (numStr.split(".").length === 2 && numStr.split(".")[1].length <= 2 ? "." : null);
    const groupSep = decimalSep === "," ? "." : ",";
    const cleaned = numStr.replaceAll(groupSep, "").replace(",", ".");
    const target = parseFloat(cleaned);
    if (isNaN(target)) { setDisplay(value); return; }
    const decimals = decimalSep && cleaned.includes(".") ? cleaned.split(".")[1].length : 0;

    const formatNum = (n) => {
      const fixed = n.toFixed(decimals);
      const [whole, frac] = fixed.split(".");
      // Re-insert thousand separators if original had them
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep === "," ? "," : ".");
      return prefix + (frac ? `${grouped}${decimalSep}${frac}` : grouped) + suffix;
    };

    const initial = prefix + (decimals > 0 ? "0" + decimalSep + "0".repeat(decimals) : "0") + suffix;
    setDisplay(initial);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              const current = target * eased;
              setDisplay(formatNum(current));
              if (t < 1) requestAnimationFrame(tick);
              else setDisplay(formatNum(target));
            };
            requestAnimationFrame(tick);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display}</span>;
}
